import {FC} from 'react';
import {type IBrand} from '@verdocs/js-sdk';
import {VerdocsButton} from '@verdocs/web-sdk-react';
import {CheckCircleIcon, LockClosedIcon} from '@heroicons/react/24/solid';
import {BrowserTabBar} from './MockChrome';

export type THostingState = 'default' | 'pending' | 'active';

const DEFAULT_HOST = 'app.verdocs.com';

const Skeleton: FC<{className: string}> = ({className}) => <div className={`h-2 rounded-full bg-gray-100 ${className}`} />;

const PageHeader: FC<{color: string; logoUrl?: string | null}> = ({color, logoUrl}) => (
  <div className="flex h-9 items-center px-3" style={{background: color}}>
    {logoUrl ? <img src={logoUrl} alt="" className="h-5 max-w-[80px] object-contain" /> : <div className="h-4 w-14 rounded bg-white/60" />}
  </div>
);

const PageBody: FC<{buttonColor: string}> = ({buttonColor}) => (
  <div className="flex flex-col gap-3 px-6 py-5">
    <Skeleton className="w-3/4" />
    <Skeleton className="w-11/12" />
    <Skeleton className="w-5/6" />
    <div className="h-7 w-32 rounded border-2 border-dashed border-gray-300" />
    <Skeleton className="w-2/3" />
    <Skeleton className="w-4/5" />
    <div className="mt-1 h-6 w-24 self-end rounded" style={{background: buttonColor}} />
  </div>
);

/**
 * A stand-in for the signing page as a recipient opens it. Only the address bar is real: it shows the host signing
 * links use today, amber while a custom domain is still being set up.
 */
export const SigningLinkPreview: FC<{brand: IBrand; hostingState: THostingState}> = ({brand, hostingState}) => {
  const host = hostingState === 'active' && brand.app_domain ? brand.app_domain : DEFAULT_HOST;
  const logoUrl = brand.thumbnail_url || brand.full_logo_url;

  return (
    <div>
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">When a recipient opens a link</h4>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <BrowserTabBar compact title={brand.page_title || brand.name || brand.key} faviconUrl={brand.favicon_url} />

        <div className="border-b border-gray-100 px-4 py-3">
          <div className="-mx-2 rounded-lg bg-indigo-50/70 px-2 py-1.5 ring-1 ring-indigo-100">
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-indigo-500">Address</span>
            <span className="flex items-center gap-1 break-all text-[13px] leading-snug">
              <LockClosedIcon className="size-3 shrink-0 text-gray-400" />
              <span>
                <span className="text-gray-400">https://</span>
                <b className={`font-semibold ${hostingState === 'pending' ? 'text-amber-600' : 'text-gray-900'}`}>{host}</b>
                <span className="text-gray-400">/sign/...</span>
              </span>
              {hostingState === 'active' && <CheckCircleIcon className="size-3.5 shrink-0 text-green-600" />}
            </span>
          </div>
        </div>

        <div className="opacity-30 [mask-image:linear-gradient(to_bottom,black_40%,transparent)]">
          <PageHeader color={brand.primary_color || '#33364b'} logoUrl={logoUrl} />
          <PageBody buttonColor={brand.secondary_color || '#55bc81'} />
        </div>
      </div>

      <p className={`mt-2.5 text-xs leading-relaxed ${hostingState === 'pending' ? 'text-amber-700' : 'text-gray-500'}`}>
        {hostingState === 'default' && 'Signing links open on the Verdocs domain. Add a custom domain to use your own.'}
        {hostingState === 'pending' && `Still opening on ${DEFAULT_HOST}. Switches to ${brand.app_domain} once the record verifies.`}
        {hostingState === 'active' && 'Signing links open on your domain.'}
      </p>
    </div>
  );
};

interface IFooterLinks {
  poweredByLabel: string;
  poweredByUrl: string;
  termsUrl: string;
  privacyUrl: string;
}

interface SigningPagePreviewProps {
  footer: IFooterLinks;
  pageTitle: string;
  faviconUrl?: string | null;
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string | null;
  onOpenBuilder: () => void;
}

/**
 * The Signing tab's stand-in for the page. The header shows the logo and the footer tracks the links as they are typed;
 * the body stays a faint impression because the Theme Editor behind the button owns the look.
 */
export const SigningPagePreview: FC<SigningPagePreviewProps> = ({
  footer,
  pageTitle,
  faviconUrl,
  primaryColor,
  secondaryColor,
  logoUrl,
  onOpenBuilder,
}) => (
  <div>
    <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">What signers see</h4>
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <BrowserTabBar compact title={pageTitle} faviconUrl={faviconUrl} />
      <div className="p-3">
        <div className="overflow-hidden rounded-lg ring-1 ring-indigo-100">
          <PageHeader color={primaryColor} logoUrl={logoUrl} />
        </div>
      </div>
      <div className="relative">
        <div className="opacity-30 [mask-image:linear-gradient(to_bottom,black_40%,transparent)]">
          <PageBody buttonColor={secondaryColor} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <VerdocsButton label="Theme Editor" size="small" onClick={onOpenBuilder} />
        </div>
      </div>
      <div className="px-3 pb-3">
        <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[10px] leading-tight text-gray-500 ring-1 ring-indigo-100">
          <span className={`truncate ${footer.poweredByUrl.trim() ? 'text-indigo-600' : ''}`}>
            {footer.poweredByLabel.trim() || <span className="italic text-gray-300">Organization default</span>}
          </span>
          <span className="flex-1" />
          {footer.termsUrl.trim() && <span className="shrink-0 text-indigo-600">Terms of use</span>}
          {footer.privacyUrl.trim() && <span className="shrink-0 text-indigo-600">Privacy policy</span>}
        </div>
      </div>
    </div>
    <p className="mt-2.5 text-xs leading-relaxed text-gray-500">
      The tab title, favicon, and footer follow your edits as you type. Colors, fonts, and the rest of the look come from the Theme Editor.
    </p>
  </div>
);
